import React from 'react';

/**
 * Iter 66.1: ErrorBoundary used to wrap risky panels (e.g. Job Details
 * dialog body) so a single bad record never white-screens the entire SPA.
 * The fallback shows the actual error message + a "Copy" button so users
 * can paste it back to support without dev tools.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[SHS][ErrorBoundary]', error, info);
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null });

  copy = () => {
    try {
      const text = `${this.state.error?.message || this.state.error}\n\n${this.state.info?.componentStack || ''}`;
      navigator.clipboard?.writeText(text);
    } catch { /* ignore */ }
  };

  render() {
    if (!this.state.error) return this.props.children;
    const label = this.props.label || 'this section';
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900 space-y-2"
        data-testid="error-boundary-fallback"
      >
        <p className="font-semibold">Something went wrong rendering {label}.</p>
        <pre className="whitespace-pre-wrap break-words text-xs bg-white/70 rounded p-2 max-h-40 overflow-auto">
          {String(this.state.error?.message || this.state.error)}
        </pre>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={this.reset}
            className="px-3 py-1 rounded bg-red-600 text-white text-xs hover:bg-red-700"
            data-testid="error-boundary-retry"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={this.copy}
            className="px-3 py-1 rounded border border-red-300 text-red-800 text-xs hover:bg-red-100"
            data-testid="error-boundary-copy"
          >
            Copy error
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
